"use client"
import { CSSProperties, Fragment, useEffect, useState, type ReactNode } from "react"
import { T } from "gt-next"
import Script from "next/script"
import clsx from "clsx"
import { hasCookie } from "cookies-next"
import Anchor from "@shared-ui/components/Anchor"
import { FaInstagram } from "react-icons/fa"

export default function GalaxyButton({ className, title, url, customTextStyle }: { className?: string; title?: ReactNode; url?: string; customTextStyle?: CSSProperties }) {
    // const [isLoggedIn, setIsLoggedIn] = useState(false);
    // useEffect(() => {
    //   const cookies = Object.fromEntries(document.cookie.split('; ').map(cookie => cookie.split('=')));
    //   const supabaseAuthCookie = Object.keys(cookies).find(name => /^sb-.+-auth-token$/.test(name));
    //   setIsLoggedIn(hasCookie('supabase-auth-token') || hasCookie(supabaseAuthCookie));
    // }, []);

    const buttonStyle: CSSProperties = {
        fontSize: "1.3rem",
        fontWeight: "500",
        border: "0",
        cursor: "pointer",
        padding: "0.9em 1.3em",
        display: "flex",
        alignItems: "center",
        gap: "0.25em",
        whiteSpace: "nowrap",
        borderRadius: "1.1rem",
        position: "relative",
        boxShadow: "0 0 calc(var(--active) * 6em) calc(var(--active) * 3em) hsl(var(--hue) 97% 61% / 0.5), 0 0.05em 0 0 hsl(var(--hue) calc(var(--active) * 97%) calc((var(--active) * 50%) + 30%)) inset, 0 -0.05em 0 0 hsl(var(--hue) calc(var(--active) * 97%) calc(var(--active) * 10%)) inset",
        transition: "box-shadow var(--transition), scale 150ms ease-out, background var(--transition)",
        scale: "calc((1 + (var(--active) * 0.1)) * var(--press, 1))",
        transformStyle: "preserve-3d",
        perspective: "100vmin",
        overflow: "hidden",
        background: "var(--bg)"
    }

    const sparkStyle: CSSProperties = {
        position: "absolute",
        inset: "0",
        borderRadius: "1.1rem",
        overflow: "hidden",
        mask: "linear-gradient(white, transparent 50%)"
    }

    const galaxyContainerStyle: CSSProperties = {
        position: "absolute",
        inset: "0",
        opacity: "var(--active)",
        transition: "opacity var(--transition)",
        mask: "radial-gradient(white, transparent)"
    }

    const galaxyStyle: CSSProperties = {
        position: "absolute",
        width: "100%",
        aspectRatio: "1",
        top: "50%",
        left: "50%",
        translate: "-50% -50%",
        overflow: "hidden",
        opacity: "var(--active)",
        transition: "opacity var(--transition)"
    }
    const textStyle: CSSProperties = {
        translate: customTextStyle?.translate || "2% -6%",
        letterSpacing: customTextStyle?.letterSpacing || "0.01ch",
        color: customTextStyle?.color || "hsl(0 0% calc(60% + (var(--active) * 26%)))",
        ...customTextStyle
    }

    const galaxyScript = `
    let RANDOM = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);
    let PARTICLES = document.querySelectorAll('.star');
    PARTICLES.forEach(P => {
      P.setAttribute('style', 
        '--angle: ' + RANDOM(0, 360) + ';' +
        '--duration: ' + RANDOM(6, 20) + ';' +
        '--delay: ' + RANDOM(1, 10) + ';' +
        '--alpha: ' + (RANDOM(40, 90) / 100) + ';' +
        '--size: ' + RANDOM(2, 8) + ';' +
        '--distance: ' + RANDOM(60, 400) + ';'
      );
    });
  `

    return (
        <>
            <style jsx global>{`
                :root {
                    --transition: 0.25s;
                    --spark: 1.8s;
                    --hue: 265;
                }

                .galaxy-button button {
                    --cut: 0.1em;
                    --active: 0;
                    --bg:
                        radial-gradient(120% 120% at 126% 126%, hsl(var(--hue) calc(var(--active) * 97%) 98% / calc(var(--active) * 0.9)) 40%, transparent 50%) calc(100px - (var(--active) * 100px)) 0 / 100% 100% no-repeat,
                        radial-gradient(120% 120% at 120% 120%, hsl(var(--hue) calc(var(--active) * 97%) 70% / calc(var(--active) * 1)) 30%, transparent 70%) calc(100px - (var(--active) * 100px)) 0 / 100% 100% no-repeat, hsl(var(--hue) calc(var(--active) * 100%) calc(12% - (var(--active) * 8%)));
                    background: var(--bg);
                    font-size: 1.3rem;
                    font-weight: 500;
                    border: 0;
                    cursor: pointer;
                    padding: 0.9em 1.3em;
                    display: flex;
                    align-items: center;
                    gap: 0.25em;
                    white-space: nowrap;
                    border-radius: 1.1rem;
                    position: relative;
                    box-shadow:
                        0 0 calc(var(--active) * 6em) calc(var(--active) * 3em) hsl(var(--hue) 97% 61% / 0.5),
                        0 0.05em 0 0 hsl(var(--hue) calc(var(--active) * 97%) calc((var(--active) * 50%) + 30%)) inset,
                        0 -0.05em 0 0 hsl(var(--hue) calc(var(--active) * 97%) calc(var(--active) * 10%)) inset;
                    transition:
                        box-shadow var(--transition),
                        scale 150ms ease-out,
                        background var(--transition);
                    scale: calc((1 + (var(--active) * 0.1)) * var(--press, 1));
                    transform-style: preserve-3d;
                    perspective: 100vmin;
                    overflow: hidden;
                }

                /* Press: scoped to this button (an unscoped button:active rule overrode every
                   other button's press scale on the page) */
                .galaxy-button button:active {
                    --press: 0.97;
                }

                .star {
                    height: calc(var(--size) * 1px);
                    aspect-ratio: 1;
                    background: white;
                    border-radius: 50%;
                    /*  filter: blur(4px);*/
                    position: absolute;
                    opacity: var(--alpha);
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%) rotate(10deg) rotate(0deg) translateY(calc(var(--distance) * 1px));
                    animation: orbit calc(var(--duration) * 1s) calc(var(--delay) * -1s) infinite linear;
                }

                @keyframes orbit {
                    to {
                        transform: translate(-50%, -50%) rotate(10deg) rotate(360deg) translateY(calc(var(--distance) * 1px));
                    }
                }

                .galaxy {
                    position: absolute;
                    width: 100%;
                    aspect-ratio: 1;
                    top: 50%;
                    left: 50%;
                    translate: -50% -50%;
                    overflow: hidden;
                    opacity: var(--active);
                    transition: opacity var(--transition);
                    /*transform-style: preserve-3d;
  perspective: 100vmin;*/
                    /*  opacity: 0.25;*/
                }

                .galaxy__ring {
                    height: 200%;
                    width: 200%;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    border-radius: 50%;
                    transform: translate(-28%, -40%) rotateX(-24deg) rotateY(-30deg) rotateX(90deg);
                    transform-style: preserve-3d;
                }

                .galaxy__container {
                    position: absolute;
                    inset: 0;
                    opacity: var(--active);
                    transition: opacity var(--transition);
                    mask: radial-gradient(white, transparent);
                }

                .star--static {
                    animation: none;
                    top: 50%;
                    left: 50%;
                    transform: translate(0, 0);
                    max-height: 4px;
                    filter: brightness(4);
                    opacity: 0.9;
                    animation:
                        move-x calc(var(--duration) * 0.1s) calc(var(--delay) * -0.1s) infinite linear,
                        move-y calc(var(--duration) * 0.2s) calc(var(--delay) * -0.2s) infinite linear;
                }

                button:hover .star--static {
                    animation-play-state: paused;
                }

                @keyframes move-x {
                    0% {
                        translate: -100px 0;
                    }
                    100% {
                        translate: 100px 0;
                    }
                }

                @keyframes move-y {
                    0% {
                        transform: translate(0, -50px);
                    }
                    100% {
                        transform: translate(0, 50px);
                    }
                }

                .spark {
                    position: absolute;
                    inset: 0;
                    border-radius: 1.1rem;
                    rotate: 0deg;
                    overflow: hidden;
                    mask: linear-gradient(white, transparent 50%);
                    animation: flip calc(var(--spark) * 2) infinite steps(2, end);
                }

                @keyframes flip {
                    to {
                        rotate: 360deg;
                    }
                }

                .spark:before {
                    content: "";
                    position: absolute;
                    width: 200%;
                    aspect-ratio: 1;
                    top: 0%;
                    left: 50%;
                    z-index: -1;
                    translate: -50% -15%;
                    rotate: 0;
                    transform: rotate(-90deg);
                    opacity: calc((var(--active)) + 0.4);
                    background: conic-gradient(from 0deg, transparent 0 340deg, white 360deg);
                    transition: opacity var(--transition);
                    animation: rotate var(--spark) linear infinite both;
                }

                .spark:after {
                    content: "";
                    position: absolute;
                    inset: var(--cut);
                    border-radius: 1.1rem;
                }

                .galaxy-button .backdrop {
                    position: absolute;
                    inset: var(--cut);
                    background: var(--bg);
                    border-radius: 1.1rem;
                    transition: background var(--transition);
                }

                @keyframes rotate {
                    to {
                        transform: rotate(90deg);
                    }
                }

                @supports (selector(:has(:is(+ *)))) {
                    body:has(button:is(:hover, :focus-visible)) {
                        --active: 1;
                        --play-state: running;
                    }
                    .galaxy-button .bodydrop {
                        display: none;
                    }
                }

                button:is(:hover, :focus-visible) ~ :is(.bodydrop, .particle-pen) {
                    --active: 1;
                    --play-state: running;
                }

                .galaxy-button .bodydrop {
                    background: hsl(260 calc(var(--active) * 97%) 6%);
                    position: fixed;
                    inset: 0;
                    z-index: -1;
                }

                .galaxy-button button:is(:hover, :focus-visible) {
                    --active: 1;
                    --play-state: running;
                }

                .galaxy-button {
                    position: relative;
                }

                @keyframes float-out {
                    to {
                        rotate: 360deg;
                    }
                }

                .galaxy-button .text {
                    translate: 2% -6%;
                    letter-spacing: 0.01ch;
                    /*background: linear-gradient(90deg, hsl(0 0% calc((var(--active) * 100%) + 65%)), hsl(0 0% calc((var(--active) * 100%) + 26%)));
  -webkit-background-clip: text;*/
                    color: hsl(0 0% calc(60% + (var(--active) * 26%)));
                }

                .galaxy-button button svg {
                    inline-size: 1.25em;
                    translate: -25% -5%;
                }
            `}</style>

            <Script id="galaxy-starts" strategy="afterInteractive">
                {galaxyScript}
            </Script>

            <Anchor href={url || "/setup"} className={clsx(className, "galaxy-button")}>
                <button style={buttonStyle}>
                    <span className="spark" style={sparkStyle} />
                    <span className="backdrop" />
                    <span className="galaxy__container" style={galaxyContainerStyle}>
                        <span className="star star--static" />
                        <span className="star star--static" />
                        <span className="star star--static" />
                        <span className="star star--static" />
                    </span>
                    <span className="galaxy" style={galaxyStyle}>
                        <span className="galaxy__ring">
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                            <span className="star" />
                        </span>
                    </span>
                    <span className="text inline-flex items-center" style={textStyle}>
                        <FaInstagram style={{ width: "1.35rem", height: "1.35rem", marginLeft: "0px", marginRight: "10px", marginTop: "1px" }} />
                        {title || <T>Start growing your Instagram</T>}
                    </span>
                </button>
                {/* <div className="bodydrop" /> */}
            </Anchor>
        </>
    )
}
